<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card class="q-dialog-plugin view-audit-dialog-card">
      <q-card-section class="row">
        <div
          class="text-h6 ellipsis col"
        >
          {{ getAuditEventType(auditLogEvent) }}
        </div>
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-card-section class="row items-center q-col-gutter-md">
        <div
          class="col-12"
        >
          <q-field
            outlined
            :label="$t('common.date')"
            stack-label
          >
            <template #control>
              <div
                class="self-center full-width no-outline"
                tabindex="0"
              >
                {{ formatAuditEventField(auditLogEvent, 'createTimestamp', userCache) }}
              </div>
            </template>
          </q-field>
        </div>
        <div
          class="col-12"
        >
          <q-field
            outlined
            :label="$t('auditLogReports.tableCols.eventType')"
            stack-label
          >
            <template #control>
              <div
                class="self-center full-width no-outline"
                tabindex="0"
              >
                {{ getAuditEventType(auditLogEvent) }}
              </div>
            </template>
          </q-field>
        </div>
        <div
          class="col-12"
        >
          <q-field
            outlined
            :label="$t('auditLogReports.tableCols.description')"
            stack-label
          >
            <template #control>
              <div
                class="self-center full-width no-outline"
                tabindex="0"
              >
                {{ getAuditEventLabel(auditLogEvent) }}
              </div>
            </template>
          </q-field>
        </div>
        <div
          v-if="!hasUserInstance"
          class="col-12"
        >
          <q-field
            outlined
            :label="$t('auditLogReports.tableCols.user')"
            stack-label
          >
            <template #control>
              <div
                class="self-center full-width no-outline"
                tabindex="0"
              >
                {{ formatAuditEventField(auditLogEvent, 'user', userCache) }}
              </div>
            </template>
          </q-field>
        </div>
        <div
          v-if="auditLogEvent?.client"
          class="col-12"
        >
          <q-field
            outlined
            :label="$t('auditLogReports.tableCols.client')"
            stack-label
            :loading="isLoadingClients"
          >
            <template #control>
              <div
                v-if="!isLoadingClients"
                class="self-center full-width no-outline"
                tabindex="0"
              >
                {{ formatAuditEventField(auditLogEvent, 'client', userCache) }}
              </div>
            </template>
          </q-field>
        </div>
        <div
          v-if="hasUserInstance"
          class="col-12"
        >
          <q-field
            outlined
            :label="$t('auditLogReports.tableCols.userInstance')"
            stack-label
          >
            <template #control>
              <div
                class="self-center full-width no-outline"
                tabindex="0"
              >
                {{ formatAuditEventField(auditLogEvent, 'userInstance', userCache) }}
              </div>
            </template>
          </q-field>
        </div>
        <div
          class="col-12"
        >
          <q-field
            outlined
            :label="$t('auditLogReports.tableCols.collection')"
            stack-label
          >
            <template #control>
              <div
                class="self-center full-width no-outline"
                tabindex="0"
              >
                {{ getAuditResourceType(auditLogEvent.collection) }}
              </div>
            </template>
          </q-field>
        </div>
        <div
          class="col-12"
        >
          <q-field
            outlined
            :label="$t('auditLogReports.tableCols.resourceId')"
            stack-label
          >
            <template #control>
              <div
                class="self-center full-width no-outline"
                tabindex="0"
              >
                {{ formatAuditEventField(auditLogEvent, 'resourceId', userCache) }}
              </div>
            </template>
          </q-field>
        </div>
        <div
          class="col-12"
        >
          <q-field
            outlined
            :label="$t('auditLogReports.tableCols.field')"
            stack-label
          >
            <template #control>
              <div
                class="self-center full-width no-outline"
                tabindex="0"
              >
                {{ formatAuditEventField(auditLogEvent, 'field', userCache) }}
              </div>
            </template>
          </q-field>
        </div>
        <div
          v-if="getAuditEventFieldType(auditLogEvent) === 'html'"
          class="col-12"
        >
          <q-card
            bordered
            flat
          >
            <q-tabs
              v-model="htmlViewTab"
              active-color="primary"
              indicator-color="primary"
              align="justify"
            >
              <q-tab
                name="oldValue"
                :label="$t('auditLogReports.tableCols.oldValue')"
              />
              <q-tab
                name="newValue"
                :label="$t('auditLogReports.tableCols.newValue')"
              />
              <q-tab
                name="diff"
                :label="$t('auditLogReports.tableCols.diff')"
              />
            </q-tabs>
            <q-separator />
            <q-tab-panels
              v-model="htmlViewTab"
              animated
            >
              <!--eslint-disable vue/no-v-text-v-html-on-component-->
              <q-tab-panel
                name="oldValue"
                v-html="auditLogEvent.oldValue"
              />
              <q-tab-panel
                name="newValue"
                v-html="auditLogEvent.newValue"
              />
              <q-tab-panel
                name="diff"
                v-html="getHtmlDiff()"
              />
              <!--eslint-enable vue/no-v-text-v-html-on-component-->
            </q-tab-panels>
          </q-card>
        </div>
        <template v-else>
          <div
            class="col-12"
          >
            <q-field
              outlined
              :label="$t('auditLogReports.tableCols.oldValue')"
              stack-label
            >
              <template #control>
                <div
                  class="self-center full-width no-outline"
                  tabindex="0"
                >
                  {{ formatAuditEventField(auditLogEvent, 'oldValue', userCache) }}
                </div>
              </template>
            </q-field>
          </div>
          <div
            class="col-12"
          >
            <q-field
              outlined
              :label="$t('auditLogReports.tableCols.newValue')"
              stack-label
            >
              <template #control>
                <div
                  class="self-center full-width no-outline"
                  tabindex="0"
                >
                  {{ formatAuditEventField(auditLogEvent, 'newValue', userCache) }}
                </div>
              </template>
            </q-field>
          </div>
        </template>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import HtmlDiff from 'htmldiff-js';
import { isEmpty } from 'lodash';
import { mapActions, mapState } from 'pinia';
import AuditLogMixin from 'src/mixins/AuditLogMixin';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';

export default {
  name: 'ViewAuditEvent',

  mixins: [AuditLogMixin, Interactions],

  props: {
    auditLogEvent: {
      type: Object,
      required: true,
    },
    userCache: {
      type: Array,
      required: true,
    },
  },

  emits: ['hide'],

  data() {
    return {
      htmlViewTab: 'diff',
      // Diff library requires non-empty HTML value, so we should replace null with empty HTML
      emptyHtmlValue: '<p dir="auto"></p>',
    };
  },

  computed: {
    ...mapState(auth, ['connectedClinic']),
    ...mapState(clients, ['isLoadingClients', 'clients']),

    hasUserInstance() {
      return this.auditLogEvent?.overrideValue?.userInstance;
    },
  },

  methods: {
    ...mapActions(clients, ['initializeClients']),

    show() {
      this.$refs.dialog.show();
      if (isEmpty(this.clients)) {
        this.initializeClients();
      }
    }, // show()

    hide() {
      this.$emit('hide');

      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    }, // hide()

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    }, // onDialogHide()

    getHtmlDiff() {
      return HtmlDiff.execute(
        this.auditLogEvent.oldValue || this.emptyHtmlValue,
        this.auditLogEvent.newValue || this.emptyHtmlValue,
      );
    },

  },
};
</script>
<style lang="scss" scoped>
.view-audit-dialog-card {
  width: 80vw;
  min-width: 375px;
  max-width: unset;
}

</style>
